import React from 'react';
import { Box, Typography, Tooltip, IconButton } from '@mui/material';
import { CheckCircle as CheckCircleIcon, ArrowForwardIos as ArrowForwardIosIcon } from '@mui/icons-material';
import StarRating from '../StarRating';

export interface NotificationItemProps {
  id: string;
  type: 'order' | 'review' | 'question' | 'other';
  title: string;
  description: string;
  timestamp: string;
  isNew: boolean;
  isUnread?: boolean;
  rating?: number;
  onMarkAsRead?: (id: string) => void;
  onClick?: () => void;
}

const NotificationItem: React.FC<NotificationItemProps> = ({
  id,
  title,
  description,
  timestamp,
  isNew,
  isUnread,
  rating,
  onMarkAsRead,
  onClick,
}) => {
  const handleMarkAsRead = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering onClick when clicking the mark-as-read button
    if (onMarkAsRead && isUnread) {
      onMarkAsRead(id);
    }
  };

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  const isClickable = !!onClick;

  return (
    <Box
      onClick={handleClick}
      sx={{
        display: 'flex',
        gap: '16px',
        padding: '16px 0',
        borderBottom: '1px solid #F1F4F4',
        position: 'relative',
        cursor: isClickable ? 'pointer' : 'default',
        borderRadius: isClickable ? '8px' : '0',
        marginX: isClickable ? '-8px' : '0',
        paddingX: isClickable ? '8px' : '0',
        '&:hover': isClickable ? {
          backgroundColor: 'rgba(103, 49, 79, 0.04)',
          transform: 'translateX(4px)',
        } : {},
        transition: 'all 0.2s ease',
        '&:last-child': {
          borderBottom: 'none',
        },
      }}
    >
      {/* Unread indicator - green check icon on the left (clickable to mark as read) */}
      {isUnread && (
        <Tooltip title="Mark as read">
          <IconButton
            onClick={handleMarkAsRead}
            size="small"
            sx={{
              position: 'absolute',
              left: '-32px',
              top: '13px',
              padding: '4px',
              color: '#426A53',
              zIndex: 1, // Ensure it's above the clickable area
              '&:hover': {
                color: '#426A53',
                backgroundColor: 'rgba(66, 106, 83, 0.08)',
                transform: 'scale(1.1)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      )}
      
      {/* New indicator dot */}
      {isNew && !isUnread && (
        <Box
          sx={{
            position: 'absolute',
            left: '-20px',
            top: '23px',
            width: '8px',
            height: '8px',
            backgroundColor: '#426A53',
            borderRadius: '50%',
          }}
        />
      )}

      {/* Content */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
          flex: 1,
        }}
      >
        <Typography
          sx={{
            fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
            fontSize: '16px',
            lineHeight: '1.4',
            fontWeight: 400,
            color: '#181818',
          }}
        >
          {title}
        </Typography>

        {rating !== undefined && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <StarRating rating={rating} size="small" showNumber />
          </Box>
        )}

        <Typography
          sx={{
            fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
            fontSize: '16px',
            lineHeight: '1.4',
            fontWeight: 400,
            color: '#181818',
            whiteSpace: 'pre-line',
          }}
        >
          {description}
        </Typography>
      </Box>

      {/* Timestamp and Click Indicator */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        <Typography
          sx={{
            fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
            fontSize: '13px',
            lineHeight: '1.4',
            fontWeight: 400,
            color: isNew ? '#181818' : '#93989C',
            whiteSpace: 'nowrap',
          }}
        >
          {timestamp}
        </Typography>
        {isClickable && (
          <ArrowForwardIosIcon
            sx={{
              fontSize: '14px',
              color: '#93989C',
              opacity: 0.6,
              transition: 'all 0.2s ease',
            }}
          />
        )}
      </Box>
    </Box>
  );
};

export default NotificationItem;

